import { useState } from 'react'
import type { ChatMessageDto } from '@lunary/api-client'
import { useChatReply, useStudioOptions } from './queries'
import { ChatSettingsBar } from './chat-settings'
import type { ChatSettings } from './chat-settings'
import { ChatReply } from './chat-reply'
import type { MilaTurn } from './chat-reply'
import { ChatExamples } from './chat-examples'
import styles from './studio.module.css'

type Turn = { role: 'user'; content: string } | ({ role: 'assistant' } & MilaTurn)

const toMessage = (t: Turn): ChatMessageDto => ({ role: t.role, content: t.content })

export function Chat() {
  const options = useStudioOptions()
  const send = useChatReply()
  const [settings, setSettings] = useState<ChatSettings | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')

  if (options.isPending) return <div className="spinner" aria-label="Загрузка" />
  if (options.isError) return <p className="error-box">Не удалось загрузить настройки Студии.</p>
  const o = options.data
  if (o.chat_versions.length === 0) return <p className="error-box">Нет промпта задачи «диалог».</p>
  const current = settings ?? {
    model: o.default_model || o.models[0] || '',
    persona_version: o.active_persona,
    chat_version: o.active_chat,
  }

  const submit = () => {
    const text = draft.trim()
    if (text.length === 0) return
    const next: Turn[] = [...turns, { role: 'user', content: text }]
    setTurns(next)
    setDraft('')
    send.mutate(
      { ...current, messages: next.slice(-30).map(toMessage) },
      {
        onSuccess: (r) =>
          setTurns((prev) => [
            ...prev,
            { role: 'assistant', content: r.content, original: r.content, model: r.model, promptVersion: r.prompt_version, saved: false },
          ]),
      },
    )
  }

  const markSaved = (index: number, content: string) =>
    setTurns((prev) => prev.map((t, i) => (i === index && t.role === 'assistant' ? { ...t, content, saved: true } : t)))

  return (
    <div>
      <div className="card-panel">
        <ChatSettingsBar value={current} options={o} onChange={setSettings} />
        <p className="muted" style={{ fontSize: 13 }}>
          Пишите как пользователь. Любой ответ Милы можно поправить и сохранить как эталон диалога; дальше разговор
          продолжится уже с вашей версией. В приложения этот чат не выходит.
        </p>
      </div>

      <div className={styles.chat} aria-live="polite">
        {turns.length === 0 && <p className="muted">Начните диалог: например, «Мне выпала Звезда, что это значит для работы?»</p>}
        {turns.map((t, i) =>
          t.role === 'user' ? (
            <div key={i} className={`${styles.bubble} ${styles.bubbleUser}`}>
              <div className={styles.bubbleText}>{t.content}</div>
            </div>
          ) : (
            <ChatReply
              key={i}
              turn={t}
              history={turns.slice(Math.max(0, i - 30), i).map(toMessage)}
              onSaved={(content) => markSaved(i, content)}
            />
          ),
        )}
        {send.isPending && <div className="spinner" aria-label="Мила отвечает" />}
        {send.error && <p className="error-box">{send.error instanceof Error ? send.error.message : 'Модель не ответила'}</p>}
      </div>

      <div className={styles.chatInput}>
        <textarea
          rows={3}
          value={draft}
          maxLength={4000}
          placeholder="Сообщение от лица пользователя"
          aria-label="Сообщение"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
          }}
        />
        <div className="row">
          <button disabled={send.isPending || draft.trim().length === 0 || turns.at(-1)?.role === 'user'} onClick={submit}>
            Отправить
          </button>
          <button className="secondary" disabled={turns.length === 0 || send.isPending} onClick={() => setTurns([])}>
            Новый диалог
          </button>
        </div>
      </div>

      <ChatExamples />
    </div>
  )
}
